import { useEffect, useState } from "react";
import CiriumLogo from "@/assets/images/cirium-logo.png";
import { Icon, Typography } from "@/components/atoms";
import { FlightDetails, FlightLeg } from "@/redux/apis/clientFlight/types";
import { clsx } from "@/utils";
import { FlightPickupDisclosure, FlightPickupFooter, hasGateDrift } from "./FlightPickupFooter";
import { FlightTrackingDot } from "./FlightTrackingDot";
import {
  LandingSource,
  flightDuration,
  formatFlightDate,
  formatFlightTime,
  formatFlightWeekdayDate,
  formatUpdatedAgo,
  legTime,
  legTimeLabel,
  resolveLanding,
  resolveLegTime,
  scheduledLegTime,
} from "./flightPickup";

/** "6:00 PM", plus the date when it falls on a different day from `other` — a clock alone would hide a day's move. */
const clockAgainst = (iso: string, other: string): string =>
  formatFlightDate(iso) === formatFlightDate(other) ? formatFlightTime(iso) : `${formatFlightTime(iso)}, ${formatFlightDate(iso)}`;

/**
 * One end of the flight in the card's times row (RM-Client 7159:80): "Sydney · Thu 30 Jul", the label, the
 * live time, and — when the live time differs from the schedule — the schedule struck through beneath it,
 * with the live time red when late and green when early.
 *
 * The live time is the shared gate-first chain (`legTime`), the same value the pickup is derived from, so
 * the card and the pickup box can never name different arrivals. The schedule is the GATE schedule, or the
 * published one where no gate schedule exists yet (`scheduledLegTime`).
 *
 * Moved-or-not is judged on the rendered wall clock, which is what the reader compares: an estimate echoing
 * its schedule — the API does this before departure — strikes nothing.
 *
 * On a disrupted flight the label is the disruption and the scheduled time is struck, uncoloured: the
 * flight is not operating, so no time on it is a claim.
 */
const LegTimes = ({
  leg,
  end,
  disruptionStatus,
}: {
  leg: FlightLeg | null;
  end: "arrival" | "departure";
  disruptionStatus?: "cancelled" | "diverted" | null;
}) => {
  const live = resolveLegTime(leg);
  // The schedule of the SAME event as the live time: a gate estimate against the runway schedule would read
  // ordinary taxi-in as a delay. Runway-sourced times (no gate data yet) fall back to the published schedule.
  const gateLive = live?.source.startsWith("gate");
  const scheduled = gateLive ? scheduledLegTime({ ...leg!, scheduled: null }) : scheduledLegTime(leg);
  const anchor = live?.iso ?? scheduled;
  const place = [leg?.airport?.city || leg?.airport?.name, anchor ? formatFlightWeekdayDate(anchor) : null].filter(Boolean).join(" · ");
  const moved = !disruptionStatus && !!live && !!scheduled && clockAgainst(live.iso, scheduled) !== formatFlightTime(scheduled);
  const tone = moved ? (Date.parse(live!.iso) > Date.parse(scheduled!) ? "text-danger-dark" : "text-success-dark") : null;

  return (
    <div
      className={clsx("flex min-w-0 flex-1 flex-col gap-[3px]", end === "departure" ? "pr-3" : "border-l border-neutral-mid-gray pl-3.5")}
    >
      {place && (
        <Typography variant="caption" className="text-neutral-dark-gray">
          {place}
        </Typography>
      )}
      {disruptionStatus ? (
        <>
          <Typography variant="caption" className="text-danger-dark">
            {disruptionStatus === "cancelled" ? "Cancelled" : "Diverted"}
          </Typography>
          {scheduled && (
            <Typography variant="title" className="leading-5 text-neutral-silver-gray line-through">
              {formatFlightTime(scheduled)}
            </Typography>
          )}
        </>
      ) : (
        <>
          <Typography variant="caption" className={tone ?? "text-neutral-silver-gray"}>
            {legTimeLabel(live?.source, end)}
          </Typography>
          <Typography variant="title" className={clsx("leading-5", tone ?? "text-neutral-black")}>
            {live ? formatFlightTime(live.iso) : "—"}
          </Typography>
          {moved && (
            <Typography variant="caption" className="text-neutral-silver-gray line-through">
              {clockAgainst(scheduled!, live!.iso)}
            </Typography>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Google-style flight status card (RM-Client 7159:80 "Flight & Pickup / Flight Status Card"):
 * airline + number header, the two airports with the duration between them, each end's live time against
 * its schedule, and an "Updated X ago · Powered by Cirium" footer. Purely presentational — the Date & Time
 * panel owns fetching and the loading/not-found states.
 *
 * Given a {@link pickupDisclosure} with drift to explain, the disclosure block sits above the footer and
 * the header grows a live-tracking dot (RM-Client 7039-22079).
 */
export const FlightDetailsCard = ({
  flight,
  disruptionStatus,
  leg = "arrival",
  pickupDisclosure,
}: {
  flight: FlightDetails;
  /**
   * @deprecated No reader since DEV-1878. It drove the `booked from …` line under the old `Lands` footer;
   * the v2 card has no such line, and the pickup box below the card states the arrival the pickup was
   * built from instead.
   */
  frozenArrival?: { iso: string; source?: LandingSource | null } | null;
  /**
   * A cancelled or diverted flight. Both ends read the disruption in place of their label, with the
   * scheduled times struck through (RM-Client 7160:25791). Rendered even when no landing resolves, which
   * is the ordinary case — a cancelled flight is usually pulled from the schedule.
   */
  disruptionStatus?: "cancelled" | "diverted" | null;
  /**
   * Which end of the flight this card is about (DEV-1487). `"arrival"` — the default — is the pickup's;
   * `"departure"` is the drop-off's. The card draws the whole flight either way; only the pickup
   * disclosure and the tracking dot are arrival-only.
   */
  leg?: "arrival" | "departure";
  /**
   * The pickup this arrival is answering for. Given, the card grows a live-tracking dot beside the flight
   * number and, while the gate time has drifted from the booked one, the disclosure block above the footer.
   *
   * Arrival leg only, and absent on a disrupted flight: a pickup derived from a cancelled flight does not
   * exist.
   */
  pickupDisclosure?: FlightPickupDisclosure | null;
}) => {
  // The one arrival on this card: the leg row resolves it the same way, and the footer prints it again.
  const live = resolveLanding(flight);
  // The span between the two times the card PRINTS, deliberately, and not the airborne leg.
  //
  // Running it runway-to-runway keeps taxi-in out of the number — right about an on-time 8h leg being
  // overstated as "8h 25m", wrong about this slot. The card never calls the number airborne time; it sits
  // between two clocks, and a reader who subtracts one from the other has to get it. RM-Client 5537-121254
  // reads it that way too: 11:25 AM → 1:25 PM is "2h".
  //
  // Gate-to-gate would give both, and does not exist: only the ARRIVAL leg carries gate times.
  // `FlightDeparture` has no gate columns and `Flight::$departureGate` is a gate NUMBER ("B12"). So the
  // departure end is the runway chain, which `legTime` falls through to on its own.
  //
  // The right-hand end is `live` rather than a second `legTime(flight.arrival)`: the same value, but taken
  // from the resolution the footer prints, so the span cannot end anywhere but where the card says it does.
  const duration = flightDuration(legTime(flight.departure), live?.iso ?? null);
  const departing = leg === "departure";
  // The disclosure block and the tracking dot speak for a pickup; the departure card and a disrupted flight
  // have none.
  /**
   * Is this card speaking for a tracked pickup at all? The base both the footer and the dot narrow from, so
   * neither can be shown on a card that is not — the departure card, a disrupted flight, or one with no
   * pickup derived from it.
   */
  const pickupTracked = !!pickupDisclosure && !departing && !disruptionStatus;
  /**
   * The disclosure block is for explaining a GAP, so it renders only where there is one. A flight running to
   * schedule has nothing to disclose, and `Pickup 5:22 PM / Gate Time 5:22 PM` is one fact stated twice —
   * seen on J000547, J000549 and J000550. The ordinary `Lands … • Terminal …` footer takes its place, which
   * is also what puts the arrival TERMINAL back on screen for every booking with nothing to explain.
   *
   * By construction this makes the block an edit/view-time affordance: a booking stores the live arrival as
   * its booked one at create, so `booked == live` and there is no drift until the flight moves.
   */
  const showDisclosure = pickupTracked && hasGateDrift(pickupDisclosure!);
  /**
   * Is there anything left to TRACK? The footer explains a pickup that has already been derived and is
   * worth reading whatever the flight has done since; the dot claims something is still happening, and its
   * tooltip promises "if the arrival time changes, we'll adjust your pickup". Once the arrival is final
   * that promise cannot be kept, and an animated indicator making it is worse than no indicator.
   *
   * Keyed on the resolved landing's SOURCE rather than inferred from `status`, which is this file's
   * established rule — `arrivalLabel` and `LegBlock` both say so: a `landed` status does not imply a gate
   * time exists, and a `scheduled` status beside a gate schedule is still only a schedule. An ACTUAL of
   * either kind is an event that has happened, so the time cannot move again. `gateActual` is the at-gate
   * case `isFlightPickupFrozen` already treats as a frozen pickup; `actual` is wheels-down, included on the
   * same reasoning rather than left to `status` to catch.
   *
   * `status === "landed"` stays as a second term because the two can disagree: a lookup can report the
   * flight down while the only arrival column it filled is still an estimate.
   *
   * Narrowed from `pickupTracked`, NOT from `showDisclosure`. The dot means "we have live data on this
   * flight", which is true before anything has drifted — tying it to the drift block would light it only
   * once it had nothing left to warn about.
   */
  const arrivalIsFinal = flight.status === "landed" || live?.source === "gateActual" || live?.source === "actual";
  /**
   * Is anything actually being tracked YET? "Tracking QF2 live" has to be true when it is said, and on a
   * flight days out it is not: the card resolves a published timetable and a pulsing dot claims a feed that
   * does not exist.
   *
   * The resolved landing's SOURCE cannot answer this, which is what the first attempt got wrong. It names
   * the field that won the gate-first chain, not whether the data behind it is live — and the API echoes the
   * schedule into the estimate for flights that have not departed:
   *
   *   EK414 23 Sep  scheduled  gate_scheduled 22:05  gate_estimated 22:05
   *   QF2   23 Sep  scheduled  gate_scheduled 05:10  gate_estimated 05:10
   *
   * So the honest test is that the aircraft is airborne, or the feed has actually MOVED an estimate off its
   * schedule. Compared leg-local — `gateEstimated` against `gateScheduled`, `estimated` against `scheduled`
   * — rather than against the resolved value, which may have come from a different rung.
   *
   * Compared as INSTANTS, deliberately opposite to the footer's wall-clock rule: a one-minute estimate
   * change renders the same clock but is still the feed telling us something. The footer asks whether the
   * READER sees two different times; this asks whether anything is being tracked.
   *
   * An estimate with no schedule beside it reads as NOT live — there is nothing to have moved from, and
   * claiming a feed on that basis is the guess this whole condition exists to stop.
   *
   * Losing the dot before departure costs nothing: the times row carries the flight's state, late in red
   * and early in green, in the pre-departure case too.
   */
  const movedOffSchedule = (estimate?: string | null, scheduled?: string | null): boolean => {
    if (!estimate || !scheduled) return false;
    const [a, b] = [Date.parse(estimate), Date.parse(scheduled)];
    // An unparseable timestamp yields NaN, and `NaN !== NaN` is true — so without this a malformed pair
    // reads as "the feed moved the estimate" and lights the dot, which is the guess this condition exists
    // to prevent. Unreadable data is not live data.
    return !Number.isNaN(a) && !Number.isNaN(b) && a !== b;
  };
  const arrivalIsLive =
    flight.status === "en-route" ||
    movedOffSchedule(flight.arrival?.gateEstimated, flight.arrival?.gateScheduled) ||
    movedOffSchedule(flight.arrival?.estimated, flight.arrival?.scheduled);
  const showTrackingDot = pickupTracked && arrivalIsLive && !arrivalIsFinal;

  // Re-rendered each minute so "Updated 2 min ago" ages with the data rather than freezing at first render.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const updatedAgo = formatUpdatedAgo(flight.lastUpdated, now);

  return (
    // Named by flight and direction: a booking with a flight at each end shows two cards for what is often
    // one flight number, and "departing" is what tells them apart.
    <div
      role="group"
      aria-label={`Flight ${flight.number}${departing ? " departing" : ""}`}
      className="w-full overflow-hidden rounded-lg border border-neutral-mid-gray bg-white"
    >
      <div className="flex items-center justify-between border-b border-neutral-mid-gray px-4 py-3">
        <Typography variant="title" className="leading-5 text-danger-dark">
          {(flight.airline?.name ?? flight.number).toUpperCase()}
        </Typography>
        <div className="flex items-center gap-2">
          <Typography variant="title" className="leading-5 text-neutral-dark-gray">
            {flight.number}
          </Typography>
          {/* Only where a pickup is attached to this arrival AND the arrival can still move. On the create
              path, the departure card and a disrupted flight there is nothing being tracked ON THE
              BOOKING'S BEHALF, and a dot meaning "this flight is moving" rather than "your pickup follows
              it" is the wider, vaguer claim of the two. */}
          {showTrackingDot && <FlightTrackingDot flightNumber={flight.number} />}
        </div>
      </div>

      <div className="flex flex-col gap-3.5 p-4">
        <div className="flex items-center gap-2.5">
          <Typography variant="title" className="!text-[22px] leading-7 text-neutral-black">
            {flight.departure?.airport?.iata ?? "—"}
          </Typography>
          <div className="flex min-w-0 flex-1 flex-col items-center gap-2">
            {/* Derived from times a disrupted flight no longer keeps, so it would assert the same untruth. */}
            <Typography variant="caption" className={clsx("text-neutral-dark-gray", (disruptionStatus || !duration) && "invisible")}>
              {(!disruptionStatus && duration) || "—"}
            </Typography>
            <div className="flex w-full items-center gap-1.5 text-neutral-silver-gray">
              <span aria-hidden className="min-w-0 flex-1 border-t border-dashed border-neutral-silver-gray" />
              <Icon name="Airplane" size="sm" className="rotate-90" />
              <span aria-hidden className="min-w-0 flex-1 border-t border-dashed border-neutral-silver-gray" />
            </div>
          </div>
          <Typography variant="title" className="!text-[22px] leading-7 text-neutral-black">
            {flight.arrival?.airport?.iata ?? "—"}
          </Typography>
        </div>

        <div className="flex items-start">
          <LegTimes leg={flight.departure} end="departure" disruptionStatus={disruptionStatus} />
          <LegTimes leg={flight.arrival} end="arrival" disruptionStatus={disruptionStatus} />
        </div>
      </div>

      {showDisclosure && <FlightPickupFooter disclosure={pickupDisclosure!} />}

      <div className="flex items-center justify-between gap-2 border-t border-neutral-mid-gray bg-neutral-surface-gray px-4 py-2.5">
        <Typography variant="caption" className="text-neutral-silver-gray">
          {updatedAgo ?? ""}
        </Typography>
        <span className="flex shrink-0 items-center gap-1.5">
          <Typography variant="caption" className="text-neutral-silver-gray">
            Powered by
          </Typography>
          <span className="flex items-center gap-1">
            <img src={CiriumLogo} alt="" aria-hidden className="h-4 w-[18px] object-contain" />
            <Typography variant="small" className="leading-4 text-neutral-dark-gray">
              Cirium
            </Typography>
          </span>
        </span>
      </div>
    </div>
  );
};
